/**
 * @module services/offlineSync
 * @description Automatic sync service for EmberPOS offline transactions.
 * When connectivity resumes, this service pushes all queued offline
 * transactions (orders, payments, order updates) to the server API
 * in chronological order.
 *
 * Integrates with:
 *  - connectivityService: subscribes to online/offline status changes
 *  - offlineDb: reads from and removes items from the IndexedDB queues
 */

import { onStatusChange, isOnline } from './connectivityService';
import {
  getPendingOrders,
  getPendingPayments,
  getPendingOrderUpdates,
  removePendingOrder,
  removePendingPayment,
  removePendingOrderUpdate,
  remapPendingPaymentsForOrder,
  logSync,
  getPendingSummary,
} from './offlineDb';
import { apiFetch } from '../lib/api';

/** @type {boolean} */
let isSyncing = false;

/** @type {Set<Function>} */
const syncListeners = new Set();

/** @type {Function|null} */
let unsubConnectivity = null;

/**
 * Notify sync listeners of progress.
 * @param {Object} event
 */
function notifySyncListeners(event) {
  syncListeners.forEach((fn) => {
    try {
      fn(event);
    } catch (err) {
      console.error('[OfflineSync] Listener error:', err);
    }
  });
}

/**
 * Build request headers for replayed offline traffic.
 * Includes location context when available.
 * @param {string|number|undefined} locationId
 * @returns {Record<string, string>}
 */
function buildSyncHeaders(locationId) {
  const headers = { 'Content-Type': 'application/json' };
  if (locationId !== undefined && locationId !== null && locationId !== '') {
    headers['X-Location-Id'] = String(locationId);
  }
  return headers;
}

/**
 * Attempt to sync a single pending order to the server.
 * @param {Object} pendingOrder
 * @returns {Promise<boolean>} true if synced successfully
 */
async function syncOrder(pendingOrder) {
  try {
    const {
      offlineId,
      createdAt,
      status: _status,
      locationId,
      ...orderPayload
    } = pendingOrder;

    const response = await apiFetch(`/api/orders`, {
      method: 'POST',
      headers: buildSyncHeaders(locationId),
      body: JSON.stringify(orderPayload),
    });

    if (!response.ok) {
      console.error(
        `[OfflineSync] Failed to sync order #${offlineId}:`,
        response.status
      );
      return false;
    }

    const data = await response.json();

    // Also send to kitchen if the order was created successfully
    if (data.success && data.order?.id) {
      try {
        await apiFetch(`/api/orders/${data.order.id}/send`, {
          method: 'POST',
          headers: buildSyncHeaders(locationId),
        });
      } catch {
        // Non-critical; the order was at least created
        console.warn(
          `[OfflineSync] Order #${offlineId} created but failed to send to kitchen`
        );
      }

      // Re-link any queued payments that still reference this offline order ID.
      await remapPendingPaymentsForOrder(`offline-${offlineId}`, data.order.id);
    }

    // Remove from pending queue
    await removePendingOrder(offlineId);
    await logSync('order', {
      offlineId,
      serverId: data.order?.id,
      tableId: orderPayload.tableId,
      itemCount: orderPayload.items?.length || 0,
      locationId: locationId ?? null,
      originalCreatedAt: createdAt,
    });

    return true;
  } catch (err) {
    console.error(`[OfflineSync] Error syncing order #${pendingOrder.offlineId}:`, err);
    return false;
  }
}

/**
 * Attempt to sync a single pending payment to the server.
 * @param {Object} pendingPayment
 * @returns {Promise<boolean>} true if synced successfully
 */
async function syncPayment(pendingPayment) {
  try {
    const { offlineId, createdAt, orderId, locationId, ...paymentPayload } =
      pendingPayment;

    if (!orderId) {
      console.error(`[OfflineSync] Payment #${offlineId} missing orderId`);
      return false;
    }

    if (typeof orderId === 'string' && orderId.startsWith('offline-')) {
      console.warn(
        `[OfflineSync] Payment #${offlineId} still references unresolved orderId ${orderId}`
      );
      return false;
    }

    // If we have a real server orderId, close it
    const response = await apiFetch(`/api/orders/${orderId}/close`, {
      method: 'POST',
      headers: buildSyncHeaders(locationId),
      body: JSON.stringify(paymentPayload),
    });

    if (!response.ok) {
      console.error(
        `[OfflineSync] Failed to sync payment #${offlineId}:`,
        response.status
      );
      return false;
    }

    await removePendingPayment(offlineId);
    await logSync('payment', {
      offlineId,
      orderId,
      method: paymentPayload.paymentMethod,
      total: paymentPayload.total,
      locationId: locationId ?? null,
      originalCreatedAt: createdAt,
    });

    return true;
  } catch (err) {
    console.error(
      `[OfflineSync] Error syncing payment #${pendingPayment.offlineId}:`,
      err
    );
    return false;
  }
}

/**
 * Attempt to sync a single pending order update to the server.
 * @param {Object} pendingUpdate
 * @returns {Promise<boolean>} true if synced successfully
 */
async function syncOrderUpdate(pendingUpdate) {
  try {
    const { offlineId, createdAt, endpoint, method, body, locationId } =
      pendingUpdate;

    const response = await apiFetch(`${endpoint}`, {
      method: method || 'POST',
      headers: buildSyncHeaders(locationId),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      console.error(
        `[OfflineSync] Failed to sync update #${offlineId}:`,
        response.status
      );
      return false;
    }

    await removePendingOrderUpdate(offlineId);
    await logSync('order_update', {
      offlineId,
      endpoint,
      method,
      locationId: locationId ?? null,
      originalCreatedAt: createdAt,
    });

    return true;
  } catch (err) {
    console.error(
      `[OfflineSync] Error syncing update #${pendingUpdate.offlineId}:`,
      err
    );
    return false;
  }
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Run a full sync of all pending offline transactions.
 * Processes orders first, then order updates, then payments.
 * @returns {Promise<{synced: number, failed: number, total: number}>}
 */
export async function syncAll() {
  if (isSyncing) {
    console.log('[OfflineSync] Sync already in progress, skipping');
    return { synced: 0, failed: 0, total: 0 };
  }

  if (!isOnline()) {
    console.log('[OfflineSync] Offline, cannot sync');
    return { synced: 0, failed: 0, total: 0 };
  }

  isSyncing = true;
  let synced = 0;
  let failed = 0;

  try {
    const summary = await getPendingSummary();
    const total = summary.total;

    if (total === 0) {
      isSyncing = false;
      return { synced: 0, failed: 0, total: 0 };
    }

    console.log(
      `[OfflineSync] Starting sync: ${summary.orders} orders, ${summary.payments} payments, ${summary.updates} updates`
    );
    notifySyncListeners({ type: 'sync_start', total });

    // 1. Sync orders (most important - creates server-side records)
    const pendingOrders = await getPendingOrders();
    for (const order of pendingOrders) {
      const ok = await syncOrder(order);
      if (ok) synced++;
      else failed++;
      notifySyncListeners({
        type: 'sync_progress',
        synced,
        failed,
        total,
        current: 'order',
      });
    }

    // 2. Sync order updates (status changes, etc.)
    const pendingUpdates = await getPendingOrderUpdates();
    for (const update of pendingUpdates) {
      const ok = await syncOrderUpdate(update);
      if (ok) synced++;
      else failed++;
      notifySyncListeners({
        type: 'sync_progress',
        synced,
        failed,
        total,
        current: 'order_update',
      });
    }

    // 3. Sync payments (close orders)
    const pendingPayments = await getPendingPayments();
    for (const payment of pendingPayments) {
      const ok = await syncPayment(payment);
      if (ok) synced++;
      else failed++;
      notifySyncListeners({
        type: 'sync_progress',
        synced,
        failed,
        total,
        current: 'payment',
      });
    }

    console.log(
      `[OfflineSync] Sync complete: ${synced} synced, ${failed} failed out of ${total}`
    );
    notifySyncListeners({ type: 'sync_complete', synced, failed, total });

    return { synced, failed, total };
  } catch (err) {
    console.error('[OfflineSync] Sync error:', err);
    notifySyncListeners({ type: 'sync_error', error: err.message });
    return { synced, failed, total: synced + failed };
  } finally {
    isSyncing = false;
  }
}

/**
 * Initialize the sync service. Subscribes to connectivity changes
 * and triggers a sync when the app comes back online.
 */
export function initSync() {
  if (unsubConnectivity) return; // already initialized

  unsubConnectivity = onStatusChange(async (newStatus) => {
    if (newStatus === 'online') {
      console.log('[OfflineSync] Connectivity restored, starting sync...');
      // Small delay to ensure stable connection
      setTimeout(() => syncAll(), 1500);
    }
  });

  // Also attempt a sync on init if we are online (for any leftover pending items)
  if (isOnline()) {
    setTimeout(() => syncAll(), 2000);
  }
}

/**
 * Subscribe to sync events (progress, completion, errors).
 * @param {Function} callback
 * @returns {() => void} Unsubscribe function
 */
export function onSyncEvent(callback) {
  syncListeners.add(callback);
  return () => syncListeners.delete(callback);
}

/**
 * Check if a sync is currently in progress.
 * @returns {boolean}
 */
export function isSyncInProgress() {
  return isSyncing;
}

/**
 * Stop the sync service (for cleanup/testing).
 */
export function stopSync() {
  if (unsubConnectivity) {
    unsubConnectivity();
    unsubConnectivity = null;
  }
}

export default {
  syncAll,
  initSync,
  onSyncEvent,
  isSyncInProgress,
  stopSync,
};
